import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import '../styles/BattleshipSpectator.css';

const LEVELS = [
  { id: 1, name: 'Level 1', par: 3 },
  { id: 2, name: 'Level 2', par: 6 },
  { id: 3, name: 'Level 3', par: 8 }
];

const TOTAL_PAR = LEVELS.reduce((s, l) => s + l.par, 0); // 17

const MinigolfSpectatorPage = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const socketRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(null);
  const animTimeoutRef = useRef(null);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const fetchGameState = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/minigolf/state`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.state) {
          setGameState(data.state);
          setLastUpdate(new Date());
        }
      }
    } catch (err) {
      console.error('Error fetching minigolf state:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const socket = io(API_BASE_URL, {
      auth: { public: true },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      fetchGameState();
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('minigolf-state-update', (data) => {
      setGameState(data);
      setLastUpdate(new Date());
    });

    socket.on('scroll-event', (data) => {
      const golfEvents = {
        'minigolf:good': { label: 'NICE SHOT!', color: '#00ff88', icon: '⛳', style: 'bounce' },
        'minigolf:smooth': { label: 'SMOOTH!', color: '#66bb6a', icon: '🎯', style: 'slide' },
        'minigolf:butter': { label: 'BUTTER!', color: '#ffd54f', icon: '🧈', style: 'melt' },
        'minigolf:money-shot': { label: 'MONEY SHOT!', color: '#00c853', icon: '💰', style: 'rain' },
        'minigolf:on-fire': { label: 'ON FIRE!', color: '#ff6d00', icon: '🔥', style: 'shake' },
        'minigolf:pro-move': { label: 'PRO MOVE!', color: '#7c4dff', icon: '🎩', style: 'spin' },
        'minigolf:ice-cold': { label: 'ICE COLD!', color: '#00b8d4', icon: '🧊', style: 'freeze' },
        'minigolf:clean': { label: 'CLEAN!', color: '#76ff03', icon: '⚡', style: 'flash' },
        'minigolf:clutch': { label: 'CLUTCH!', color: '#18ffff', icon: '💎', style: 'pulse' },
        'minigolf:textbook': { label: 'TEXTBOOK!', color: '#69f0ae', icon: '🏌️', style: 'bounce' },
        'minigolf:birdie': { label: 'BIRDIE!', color: '#ffd700', icon: '🦅', style: 'soar' },
        'minigolf:eagle': { label: 'EAGLE!', color: '#ffab00', icon: '🦅', style: 'soar' },
        'minigolf:hole-in-one': { label: 'HOLE IN ONE!', color: '#ffd700', icon: '⭐', style: 'explode' },
        'minigolf:perfect-round': { label: 'PERFECT ROUND!', color: '#ffd700', icon: '🏆', style: 'explode' },
        'minigolf:downtown': { label: 'FROM DOWNTOWN!', color: '#d500f9', icon: '🎰', style: 'zoom' },
        'minigolf:bank-shot': { label: 'BANK SHOT!', color: '#651fff', icon: '🏦', style: 'ricochet' },
        'minigolf:off-the-wall': { label: 'OFF THE WALL!', color: '#304ffe', icon: '🎱', style: 'ricochet' },
        'minigolf:slow-roller': { label: 'SLOW ROLLER IN!', color: '#00bfa5', icon: '🐌', style: 'crawl' },
        'minigolf:full-send': { label: 'FULL SEND!', color: '#ff3d00', icon: '🚀', style: 'rocket' },
        'minigolf:ricochet': { label: 'RICOCHET!', color: '#aa00ff', icon: '🪃', style: 'ricochet' },
        'minigolf:bad': { label: 'MISS!', color: '#ff4444', icon: '💀', style: 'shake' },
        'minigolf:whiff': { label: 'WHIFF!', color: '#d32f2f', icon: '💨', style: 'fade' },
        'minigolf:shanked': { label: 'SHANKED!', color: '#b71c1c', icon: '🏌️‍♂️', style: 'shake' },
        'minigolf:in-the-rough': { label: 'IN THE ROUGH!', color: '#558b2f', icon: '🌿', style: 'fade' },
        'minigolf:disaster': { label: 'DISASTER!', color: '#880e4f', icon: '💀', style: 'explode' },
        'minigolf:oof': { label: 'OOF!', color: '#e65100', icon: '😬', style: 'shake' },
        'minigolf:so-close': { label: 'SO CLOSE!', color: '#ff6f00', icon: '👀', style: 'pulse' },
        'minigolf:lip-out': { label: 'LIP OUT!', color: '#c62828', icon: '💔', style: 'shake' },
        'minigolf:off-the-rocks': { label: 'OFF THE ROCKS!', color: '#795548', icon: '🪨', style: 'ricochet' },
        'minigolf:water-hazard': { label: 'WATER HAZARD!', color: '#0277bd', icon: '🌊', style: 'splash' },
        'minigolf:in-the-sand': { label: 'BUNKER!', color: '#f9a825', icon: '🏖️', style: 'fade' },
        'minigolf:mulligan': { label: 'MULLIGAN!', color: '#9e9e9e', icon: '🔄', style: 'spin' },
        'minigolf:windmill': { label: 'WINDMILL!', color: '#2196f3', icon: '🌀', style: 'spin' },
        'minigolf:treadmill': { label: 'TREADMILL ON!', color: '#ff5722', icon: '🏃', style: 'slide' },
        'minigolf:blowers': { label: 'AIR JETS!', color: '#00bcd4', icon: '💨', style: 'shake' },
        'minigolf:through-windmill': { label: 'THROUGH THE WINDMILL!', color: '#1565c0', icon: '🌀', style: 'spin' },
        'minigolf:beat-treadmill': { label: 'BEAT THE TREADMILL!', color: '#e64a19', icon: '🏃', style: 'bounce' },
        'minigolf:dodged-jets': { label: 'DODGED THE JETS!', color: '#00838f', icon: '💨', style: 'slide' },
        'minigolf:ate-by-windmill': { label: 'EATEN BY WINDMILL!', color: '#283593', icon: '🌀', style: 'spin' },
        'minigolf:pushed-back': { label: 'PUSHED BACK!', color: '#bf360c', icon: '🏃', style: 'slide' },
        'minigolf:blown-away': { label: 'BLOWN AWAY!', color: '#006064', icon: '💨', style: 'fade' },
        'minigolf:pinball': { label: 'PINBALL MODE!', color: '#e040fb', icon: '🎰', style: 'ricochet' },
        'minigolf:all-obstacles': { label: 'EVERYTHING ON!', color: '#ff1744', icon: '🔥', style: 'explode' },
        'minigolf:chaos-mode': { label: 'CHAOS MODE!', color: '#d50000', icon: '⚡', style: 'explode' },
        'minigolf:crowd-wild': { label: 'CROWD GOES WILD!', color: '#4caf50', icon: '👏', style: 'bounce' },
        'minigolf:crowd-gasp': { label: 'CROWD GASPS!', color: '#9c27b0', icon: '😱', style: 'pulse' },
        'minigolf:crowd-laugh': { label: 'HAHAHA!', color: '#ff9800', icon: '🤣', style: 'bounce' },
        'minigolf:pressure': { label: 'THE PRESSURE!', color: '#f44336', icon: '😤', style: 'shake' },
        'minigolf:popcorn': { label: 'POPCORN TIME!', color: '#ffeb3b', icon: '🍿', style: 'bounce' },
        'minigolf:cold-blooded': { label: 'COLD BLOODED!', color: '#00e5ff', icon: '🥶', style: 'freeze' },
        'minigolf:final-putt': { label: 'FINAL PUTT!', color: '#ff6d00', icon: '🔔', style: 'pulse' },
        'minigolf:sudden-death': { label: 'SUDDEN DEATH!', color: '#d50000', icon: '⏰', style: 'shake' },
        'minigolf:victory-lap': { label: 'VICTORY LAP!', color: '#ffd700', icon: '🏁', style: 'soar' },
        'minigolf:bull-putt': { label: 'BULL MARKET PUTT!', color: '#00c853', icon: '📈', style: 'rocket' },
        'minigolf:bear-trap': { label: 'BEAR TRAP!', color: '#d32f2f', icon: '📉', style: 'shake' },
        'minigolf:dead-cat': { label: 'DEAD CAT!', color: '#ff8c00', icon: '🐱', style: 'bounce' },
        'minigolf:score': { label: 'SCORE!', color: '#00ff88', icon: '⛳', style: 'flash' },
        'minigolf:level': { label: data.eventName || 'NEXT LEVEL!', color: '#4caf50', icon: '🏌️', style: 'zoom' },
        'minigolf:end': { label: 'GAME OVER', color: '#607d8b', icon: '🏁', style: 'fade' },
      };
      const anim = golfEvents[data.type];
      if (anim) {
        setActiveAnimation({ ...anim, key: Date.now() });
        if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
        const duration = (data.duration || 3) * 1000;
        animTimeoutRef.current = setTimeout(() => setActiveAnimation(null), duration);
      }
    });

    fetchGameState();
    const fallbackInterval = setInterval(fetchGameState, 5000);

    return () => {
      socket.disconnect();
      clearInterval(fallbackInterval);
    };
  }, [fetchGameState]);

  const getSortedTeams = () => {
    if (!gameState?.teams) return [];
    return [...gameState.teams].sort((a, b) => a.total - b.total);
  };

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return { icon: '1ST', color: '#ffd700' };
      case 2: return { icon: '2ND', color: '#c0c0c0' };
      case 3: return { icon: '3RD', color: '#cd7f32' };
      default: return { icon: `#${rank}`, color: '#666' };
    }
  };

  const getScoreColor = (score, par) => {
    if (score === null || score === undefined || score === 0) return '#444';
    const diff = score - par;
    if (diff < 0) return '#00ff88'; // under par - green
    if (diff === 0) return '#ffffff'; // on par - white
    if (diff <= 2) return '#ffaa00'; // slightly over - yellow
    return '#ff4444'; // way over - red
  };

  const getScoreLabel = (score, par) => {
    if (score === null || score === undefined || score === 0) return '';
    const diff = score - par;
    if (diff < 0) return `${diff}`;
    if (diff === 0) return 'E';
    return `+${diff}`;
  };

  const renderConnectionStatus = () => (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      <span className="connection-dot"></span>
      <span className="connection-text">{connected ? 'LIVE' : 'RECONNECTING...'}</span>
    </div>
  );

  const fullscreenBtn = !isFs && (
    <button onClick={() => document.documentElement.requestFullscreen()} style={{
      position: 'fixed', top: 8, left: 8, zIndex: 99999,
      background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
      color: '#00ff41', padding: '4px 10px', fontSize: '11px',
      fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
    }}>FULLSCREEN</button>
  );

  const backNav = !isEmbed && (
    <div className="spectator-back-nav">
      <Link to="/spectate" className="back-link">&larr; Back to Spectate</Link>
    </div>
  );

  if (loading) {
    return (
      <div className="spectator-page loading">
        {fullscreenBtn}
        {backNav}
        <div className="loading-spinner"></div>
        <div className="loading-text">Connecting to Dead Cat Bounce...</div>
      </div>
    );
  }

  if (!gameState?.active) {
    return (
      <div className="spectator-page no-game">
        {fullscreenBtn}
        {backNav}
        {renderConnectionStatus()}
        <div className="no-game-icon" style={{ fontSize: '120px', opacity: 0.3 }}>🐱</div>
        <div className="no-game-text">No Active Game</div>
        <div className="no-game-subtext">Waiting for Dead Cat Bounce to start...</div>
        <div className="waiting-animation">
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
        </div>
      </div>
    );
  }

  const sortedTeams = getSortedTeams();
  const currentLevel = gameState.currentLevel || 1;

  return (
    <div className="spectator-page">
      {fullscreenBtn}
      {backNav}
      {renderConnectionStatus()}

      <div className="spectator-header">
        <h1 className="game-title" style={{
          background: 'linear-gradient(90deg, #44ff44, #00cc66, #44ff44)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>DEAD CAT BOUNCE</h1>
        <div className="status-banner playing">
          <span className="status-text">
            LEVEL {currentLevel}: {LEVELS[currentLevel - 1]?.name?.toUpperCase()}
            <span style={{ color: '#888', marginLeft: '15px' }}>
              PAR {LEVELS[currentLevel - 1]?.par}
            </span>
          </span>
        </div>
      </div>

      {/* Scorecard Table */}
      <div style={{
        maxWidth: '1200px', margin: '30px auto', padding: '0 20px',
      }}>
        <div style={{
          background: 'rgba(30, 30, 40, 0.9)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px',
          overflow: 'hidden',
        }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontFamily: 'Courier New, monospace',
          }}>
            <thead>
              <tr>
                <th style={{
                  padding: '18px 20px', textAlign: 'left', fontSize: '16px',
                  color: '#888', letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.1)',
                  width: '60px',
                }}>RANK</th>
                <th style={{
                  padding: '18px 20px', textAlign: 'left', fontSize: '16px',
                  color: '#888', letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.1)',
                }}>TEAM</th>
                {LEVELS.map(level => (
                  <th key={level.id} style={{
                    padding: '18px 12px', textAlign: 'center', fontSize: '14px',
                    borderBottom: '2px solid rgba(255,255,255,0.1)',
                    color: currentLevel === level.id ? '#00ff88' : '#888',
                    background: currentLevel === level.id ? 'rgba(0, 255, 136, 0.08)' : 'transparent',
                    letterSpacing: '1px',
                  }}>
                    <div>L{level.id}</div>
                    <div style={{
                      fontSize: '11px', color: currentLevel === level.id ? '#00ff88' : '#555',
                      marginTop: '4px',
                    }}>
                      PAR {level.par}
                    </div>
                  </th>
                ))}
                <th style={{
                  padding: '18px 20px', textAlign: 'center', fontSize: '16px',
                  color: '#ffd700', letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.1)',
                  borderLeft: '2px solid rgba(255,255,255,0.1)',
                }}>TOTAL</th>
                <th style={{
                  padding: '18px 15px', textAlign: 'center', fontSize: '14px',
                  color: '#888', letterSpacing: '1px', borderBottom: '2px solid rgba(255,255,255,0.1)',
                }}>VS PAR</th>
              </tr>
            </thead>
            <tbody>
              {sortedTeams.map((team, index) => {
                const rank = index + 1;
                const badge = getRankBadge(rank);
                const vsPar = team.total - TOTAL_PAR;
                const completedLevels = team.scores.filter(s => s !== null && s > 0).length;

                return (
                  <tr key={team.id} style={{
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    background: rank === 1 ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
                  }}>
                    <td style={{
                      padding: '16px 20px', textAlign: 'center',
                      fontSize: '22px', fontWeight: 'bold',
                      color: badge.color, letterSpacing: '2px',
                    }}>
                      {badge.icon}
                    </td>
                    <td style={{
                      padding: '16px 20px',
                      fontSize: rank <= 3 ? '24px' : '20px',
                      fontWeight: 'bold', color: '#fff',
                      textTransform: 'uppercase', letterSpacing: '3px',
                    }}>
                      {team.name}
                    </td>
                    {LEVELS.map(level => {
                      const score = team.scores[level.id - 1];
                      const hasScore = score !== null && score !== undefined && score > 0;
                      return (
                        <td key={level.id} style={{
                          padding: '16px 12px', textAlign: 'center',
                          background: currentLevel === level.id ? 'rgba(0, 255, 136, 0.05)' : 'transparent',
                        }}>
                          {hasScore ? (
                            <div>
                              <div style={{
                                fontSize: '24px', fontWeight: 'bold',
                                color: getScoreColor(score, level.par),
                              }}>
                                {score}
                              </div>
                              <div style={{
                                fontSize: '11px', marginTop: '2px',
                                color: getScoreColor(score, level.par),
                                opacity: 0.7,
                              }}>
                                {getScoreLabel(score, level.par)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#333', fontSize: '18px' }}>-</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{
                      padding: '16px 20px', textAlign: 'center',
                      fontSize: '28px', fontWeight: 'bold',
                      color: rank === 1 ? '#ffd700' : '#fff',
                      borderLeft: '2px solid rgba(255,255,255,0.1)',
                    }}>
                      {team.total || '-'}
                    </td>
                    <td style={{
                      padding: '16px 15px', textAlign: 'center',
                      fontSize: '18px', fontWeight: 'bold',
                      color: completedLevels === 0 ? '#444'
                        : vsPar < 0 ? '#00ff88'
                        : vsPar === 0 ? '#ffffff'
                        : '#ff4444',
                    }}>
                      {completedLevels === 0 ? '-'
                        : vsPar === 0 ? 'E'
                        : vsPar > 0 ? `+${vsPar}`
                        : vsPar}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Par reference row */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '20px',
          marginTop: '20px', color: '#555', fontSize: '13px',
          fontFamily: 'Courier New, monospace',
        }}>
          <span>TOTAL PAR: {TOTAL_PAR}</span>
          <span>|</span>
          <span style={{ color: '#00ff88' }}>UNDER PAR</span>
          <span style={{ color: '#fff' }}>EVEN</span>
          <span style={{ color: '#ffaa00' }}>+1-2</span>
          <span style={{ color: '#ff4444' }}>+3 OR MORE</span>
        </div>
      </div>

      {activeAnimation && (
        <div key={activeAnimation.key} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 9999,
          animation: `golfFade ${activeAnimation.style === 'explode' ? '4s' : '3s'} ease-out forwards`,
          background: activeAnimation.style === 'explode' ? `radial-gradient(circle, ${activeAnimation.color}22 0%, transparent 70%)` : 'transparent',
        }}>
          <div style={{
            fontSize: activeAnimation.style === 'explode' ? '160px' : '120px',
            animation: `golf_${activeAnimation.style || 'bounce'} 0.8s ease-out`,
          }}>{activeAnimation.icon}</div>
          <div style={{
            fontSize: activeAnimation.label.length > 16 ? '48px' : '64px',
            fontWeight: 'bold',
            color: activeAnimation.color,
            fontFamily: 'Courier New, monospace',
            textShadow: `0 0 30px ${activeAnimation.color}, 0 0 60px ${activeAnimation.color}`,
            letterSpacing: '8px',
            animation: `golf_${activeAnimation.style || 'bounce'}_text 0.6s ease-out`,
          }}>{activeAnimation.label}</div>
        </div>
      )}

      <div className="spectator-footer">
        <span className="update-time">
          Last updated: {lastUpdate?.toLocaleTimeString()}
        </span>
      </div>

      <style>{`
        @keyframes golfFade {
          0% { opacity: 0; }
          10% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes golf_bounce {
          0% { transform: scale(0) translateY(50px); }
          40% { transform: scale(1.3) translateY(-30px); }
          60% { transform: scale(0.9) translateY(10px); }
          80% { transform: scale(1.1) translateY(-5px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes golf_bounce_text {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes golf_shake {
          0% { transform: scale(0) rotate(0deg); }
          20% { transform: scale(1.2) rotate(-10deg); }
          40% { transform: scale(1) rotate(10deg); }
          60% { transform: scale(1.1) rotate(-5deg); }
          80% { transform: scale(1) rotate(3deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes golf_shake_text {
          0% { transform: translateX(-20px); opacity: 0; }
          30% { transform: translateX(10px); opacity: 1; }
          60% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        @keyframes golf_spin {
          0% { transform: scale(0) rotate(-360deg); }
          60% { transform: scale(1.2) rotate(30deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes golf_spin_text {
          0% { transform: rotateY(90deg); opacity: 0; }
          100% { transform: rotateY(0deg); opacity: 1; }
        }
        @keyframes golf_slide {
          0% { transform: translateX(-200px) scale(0.5); opacity: 0; }
          60% { transform: translateX(20px) scale(1.1); opacity: 1; }
          100% { transform: translateX(0) scale(1); }
        }
        @keyframes golf_slide_text {
          0% { transform: translateX(200px); opacity: 0; }
          60% { transform: translateX(-10px); opacity: 1; }
          100% { transform: translateX(0); }
        }
        @keyframes golf_explode {
          0% { transform: scale(0); }
          30% { transform: scale(2); }
          50% { transform: scale(0.8); }
          70% { transform: scale(1.5); }
          100% { transform: scale(1); }
        }
        @keyframes golf_explode_text {
          0% { transform: scale(0); opacity: 0; letter-spacing: 0px; }
          50% { transform: scale(1.3); opacity: 1; letter-spacing: 20px; }
          100% { transform: scale(1); letter-spacing: 8px; }
        }
        @keyframes golf_soar {
          0% { transform: translateY(100px) scale(0.3); opacity: 0; }
          50% { transform: translateY(-30px) scale(1.2); opacity: 1; }
          70% { transform: translateY(-50px) scale(1.1); }
          100% { transform: translateY(-20px) scale(1); }
        }
        @keyframes golf_soar_text {
          0% { transform: translateY(50px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes golf_pulse {
          0% { transform: scale(0); }
          25% { transform: scale(1.3); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes golf_pulse_text {
          0% { opacity: 0; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        @keyframes golf_flash {
          0% { transform: scale(0); filter: brightness(3); }
          30% { transform: scale(1.4); filter: brightness(2); }
          60% { transform: scale(0.95); filter: brightness(1); }
          100% { transform: scale(1); }
        }
        @keyframes golf_flash_text {
          0% { opacity: 0; filter: brightness(3); }
          50% { opacity: 1; filter: brightness(1.5); }
          100% { filter: brightness(1); }
        }
        @keyframes golf_ricochet {
          0% { transform: translateX(-100px) translateY(-50px) scale(0.5); }
          25% { transform: translateX(60px) translateY(30px) scale(1.1); }
          50% { transform: translateX(-30px) translateY(-20px) scale(0.95); }
          75% { transform: translateX(15px) translateY(10px) scale(1.05); }
          100% { transform: translateX(0) translateY(0) scale(1); }
        }
        @keyframes golf_ricochet_text {
          0% { transform: skewX(20deg); opacity: 0; }
          50% { transform: skewX(-10deg); opacity: 1; }
          100% { transform: skewX(0deg); }
        }
        @keyframes golf_rocket {
          0% { transform: translateY(200px) scale(0.3); }
          40% { transform: translateY(-40px) scale(1.3); }
          100% { transform: translateY(-20px) scale(1); }
        }
        @keyframes golf_rocket_text {
          0% { transform: translateY(100px) scale(0.5); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes golf_zoom {
          0% { transform: scale(5); opacity: 0; }
          40% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes golf_zoom_text {
          0% { transform: scale(3); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes golf_melt {
          0% { transform: scaleY(0) translateY(-50px); }
          40% { transform: scaleY(1.3) translateY(10px); }
          70% { transform: scaleY(0.9) translateY(-5px); }
          100% { transform: scaleY(1) translateY(0); }
        }
        @keyframes golf_melt_text {
          0% { transform: scaleY(0.3); opacity: 0; }
          100% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes golf_rain {
          0% { transform: translateY(-100px) scale(0.5); opacity: 0; }
          50% { transform: translateY(10px) scale(1.1); opacity: 1; }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes golf_rain_text {
          0% { transform: translateY(-50px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes golf_freeze {
          0% { transform: scale(0); filter: hue-rotate(0deg); }
          50% { transform: scale(1.2); filter: hue-rotate(180deg); }
          100% { transform: scale(1); filter: hue-rotate(0deg); }
        }
        @keyframes golf_freeze_text {
          0% { opacity: 0; letter-spacing: 30px; }
          100% { opacity: 1; letter-spacing: 8px; }
        }
        @keyframes golf_crawl {
          0% { transform: translateX(-200px) scale(0.5); }
          70% { transform: translateX(5px) scale(1.05); }
          85% { transform: translateX(2px) scale(1.02); }
          100% { transform: translateX(0) scale(1); }
        }
        @keyframes golf_crawl_text {
          0% { opacity: 0; }
          70% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes golf_fade {
          0% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes golf_fade_text {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes golf_splash {
          0% { transform: scale(0) translateY(50px); }
          30% { transform: scale(1.5) translateY(-20px); }
          50% { transform: scale(0.8) translateY(10px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes golf_splash_text {
          0% { transform: translateY(30px); opacity: 0; }
          50% { transform: translateY(-10px); opacity: 1; }
          100% { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MinigolfSpectatorPage;
